import { useCallback } from 'react'
import { CheckoutType, PaymentPeriod } from 'client/pages/payment_input/PaymentStepsComponent'
import { usePuzzlePacks } from 'client/pages/puzzle_packs/hooks/usePuzzlePacks'
import { SubscriptionType } from 'client/enums/SubscriptionType'
import { usePriceModels } from './usePriceModels'
import useConfig from './useConfig'

type CheckoutEventType = 'begin_checkout' | 'purchase'

type CheckoutEventSubscriptionType = {
  event: CheckoutEventType,
  checkoutType: Extract<CheckoutType, 'subscription'>,
  paymentPeriodId: PaymentPeriod,
  orderRef?: string,
  sessionId?: string,
}

type CheckoutEventPuzzlePackType = {
  event: CheckoutEventType,
  checkoutType: Extract<CheckoutType, 'puzzle-pack'>,
  puzzlePackId: number,
  orderRef?: string,
  sessionId?: string,
}

type CheckoutEvent = CheckoutEventSubscriptionType | CheckoutEventPuzzlePackType

type CheckoutItemType = {
  item_name: string | CheckoutType,
  price: number,
  quantity: number,
  item_category: string,
  item_category_2: string,
  item_variant: string,
  transaction_id?: string,
}

export const useTrackCheckout = () => {
  const { getPriceModel } = usePriceModels()
  const { getPuzzlePackById, getPuzzlePackTypeById } = usePuzzlePacks()
  const config = useConfig()

  const trackCheckoutEvent = useCallback(async (checkoutEvent: CheckoutEvent) => {
    let amount: number
    let currency: string
    let item: CheckoutItemType

    const { event, checkoutType, orderRef } = checkoutEvent

    if (checkoutType === 'puzzle-pack') {
      const puzzlePack = getPuzzlePackById(checkoutEvent.puzzlePackId)
      amount = puzzlePack?.price
      currency = config.entity?.currency?.currencyCode
      item = {
        item_name: puzzlePack?.displayName,
        price: amount,
        quantity: 1,
        item_category: 'puzzle-pack',
        item_category_2: getPuzzlePackTypeById(puzzlePack?.puzzlePackTypeId)?.name,
        item_variant: '',
      }
    } else {
      ({ amount, currency } = await getPriceModel(SubscriptionType.premium, checkoutEvent.paymentPeriodId))
      item = {
        item_name: checkoutType,
        price: amount,
        quantity: 1,
        item_category: 'subscription',
        item_category_2: '',
        item_variant: checkoutEvent.paymentPeriodId ? 'yearly' : 'monthly',
      }
    }

    if (event === 'purchase') {
      item = { ...item, transaction_id: orderRef }
    }

    if (!amount) {
      return Promise.resolve()
    }

    window.dataLayer.push({
      event,
      currency,
      value: amount,
      ecommerce: { items: [item] },
    })

    return Promise.resolve()
  }, [getPriceModel, getPuzzlePackById, getPuzzlePackTypeById, config])

  return {
    trackCheckoutEvent,
  }
}
